import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons/faSpinner';
import { faThumbsUp } from '@fortawesome/free-solid-svg-icons/faThumbsUp';
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons/faPaperPlane';
import { faReply } from '@fortawesome/free-solid-svg-icons/faReply';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/free-solid-svg-icons/faChevronUp';
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash';

import { FormHandler, Textarea } from 'Components/Form';
import Delete from 'Components/Partials/Modals/Delete';
import { ModalTrigger } from 'Components/Modal';

import { Socket, Toast, User } from 'Services';
import { getAvatarUrl } from 'Services/AuthHelpers';
import { translation } from 'Services/TranslationHelpers';
import ChatApi from 'Services/Api/MainStage/Chat';
import AdminChatApi from 'Services/Api/Admin/MainStage/Chat';
import ChatLikeApi from 'Services/Api/MainStage/ChatLike';

class ChatMessage extends React.Component {
    /**
     * @var state
     * @type {{messages: [], meta: null, working: boolean}}
     */
    state = {
        working: false,
        message: this.props.message ?? {},
        messages: [],
        meta: null,
        replying: false,
    };

    /**
     * @method componentDidMount
     */
    componentDidMount = () => {
        Socket.getConnection()
            .private(`main_stage_chat.${this.state.message.session_id}`)
            .listen('MainStageChat', (e) => {
                if (e.message.main_stage_chat_id === this.state.message.id) {
                    this.handleAddMessage(e.message);
                }
            })
            .listen('MainStageChatDeleted', (e) => this.handleDeleteMessage(e.message))
            .listen('MainStageChatLikeCreated', (e) => this.handleAddLike(e.like))
            .listen('MainStageChatLikeDeleted', (e) => this.handleRemoveLike(e.like))
            .listen('subscription_error', console.error);
    };

    /**
     * @method fetchMessages
     * @return {Promise<void>}
     */
    fetchMessages = async () => {
        if (this.state.working || (this.state.meta !== null && this.state.meta?.current_page === this.state.meta?.last_page)) {
            return;
        }

        const request = await ChatApi.get({
            session: this.state.message.session_id,
        }, {
            page: (this.state.meta?.current_page ?? 0) + 1,
            main_stage_chat_id: this.state.message.id,
        });

        if (request.success) {
            return this.setState({
                messages: request.data.meta.current_page !== 1 ? [...this.state.messages, ...request.data.data] : request.data.data,
                meta: request.data.meta,
                working: false,
            });
        }

        Toast.error();
    };

    /**
     * @method handlePostMessage
     * @param {object} data
     * @return {Promise<*>}
     */
    handlePostMessage = (data) => {
        return ChatApi.post({
            session: this.state.message.session_id,
        }, {
            ...data,
            // session_id: this.props.sessionId,
            main_stage_chat_id: this.state.message ? this.state.message.id : null,
        });
    };

    /**
     * @method handleAddLike
     * @param like
     */
    handleAddLike = (like) => {
        if (like.user_id === User.id) {
            return;
        }

        const { message } = this.state;

        if (message.id === like.chat_id) {
            return this.setState({
                message: {
                    ...message,
                    likes_count: message.likes_count + 1,
                },
            });
        }
    };

    /**
     * @method handleRemoveLike
     * @param like
     */
    handleRemoveLike = (like) => {
        if (like.user_id === User.id) {
            return;
        }

        const { message } = this.state;

        if (message.id === like.chat_id) {
            let { likes } = message;

            likes--;

            if (likes < 0) {
                likes = 0;
            }

            return this.setState({
                message: {
                    ...message,
                    likes,
                },
            });
        }
    };

    /**
     * @method handleAddMessage
     * @param {object} message
     */
    handleAddMessage = (message) => {
        const i = this.state.messages.map(m => m.id).indexOf(message.id);

        if (i === -1) {
            this.setState({
                messages: [
                    ...this.state.messages,
                    ...[message],
                ],
            });
        }
    };

    /**
     * @method handleAddMessage
     * @param {object} message
     */
    handleDeleteMessage = (message) => {
        if (this.state.messages.find(m => m.id === message.id)) {
            this.setState({
                messages: this.state.messages.filter(m => m.id !== message.id),
            });
        }
    };

    /**
     * @method handleToggleLike
     */
    handleToggleLike = async () => {
        const { message } = this.state;
        let request = null;

        if (message.user_has_liked) {
            request = await ChatLikeApi.delete({
                session: this.props.sessionId,
                chat: message.id,
                like: message.user_has_liked.id,
            });

            if (request.success) {
                return this.setState({
                    message: {
                        ...message,
                        likes_count: message.likes_count - 1,
                        user_has_liked: null,
                    },
                });
            }
        } else {
            request = await ChatLikeApi.post({
                session: this.props.sessionId,
                chat: message.id,
            });

            if (request.success) {
                return this.setState({
                    message: {
                        ...message,
                        likes_count: message.likes_count + 1,
                        user_has_liked: request.data.data,
                    },
                });
            }
        }
    };

    /**
     * @method
     * @param {object} message
     * @returns {Promise<void>}
     */
    handleDelete = async (message) => {
        const request = await AdminChatApi.delete({
            session: message.session_id,
            chat: message.id,
        });

        if (request.success) {
            return Toast.success('Message has been deleted');
        }
    };

    /**
     * @method handleLoadReplies
     */
    handleLoadReplies = async () => {
        await this.fetchMessages();

        this.setState({
            replying: true,
        });
    };

    /**
     * @method render
     * @return {JSX.Element}
     */
    render() {
        const { message, replying, messages, working, meta } = this.state;
        const { admin } = this.props;

        // Only allow first message to have replies.
        let allow_replies = !message.main_stage_chat_id;

        return (
            <div className="bg-ve-blue-5 bg-opacity-20 bg-gray-500 rounded-md p-3 mb-2 relative" key={message.id}>
                {admin && this.renderDeleteButton(message)}

                <div className="flex">
                    {window.base.features.avatar && (
                        <img
                            className="rounded-full h-8 w-8 mr-2"
                            src={getAvatarUrl(message.user)}
                            alt={message.user?.full_name}
                        />
                    )}

                    <div className="w-full">
                        <div className="flex justify-between text-xs">
                            <span>{message.user.first_name} {message.user.last_name}</span>
                        </div>

                        <div className="text-md mb-1 leading-5">
                            {message.message.split('\n').map((item, key) => (
                                <p className="mb-1" key={key}>
                                    {item}
                                </p>
                            ))}
                        </div>

                        <div className="flex items-center text-xs space-x-2">
                            <span>
                                <FontAwesomeIcon
                                    title="Reply"
                                    icon={faThumbsUp}
                                    size="1x"
                                    onClick={() => !admin ? this.handleToggleLike() : false}
                                    className={`cursor-pointer mr-1 ${message.user_has_liked ? 'text-app-blue-light' : ''}`}
                                />
                                {message.likes_count !== 0 && (
                                    <span className="text-app-blue-light">{message.likes_count}</span>
                                )}
                            </span>

                            {allow_replies && !admin && (
                                <FontAwesomeIcon
                                    title="Reply"
                                    icon={faReply}
                                    size="1x"
                                    onClick={this.handleLoadReplies}
                                    className="cursor-pointer"
                                />
                            )}
                        </div>

                        {allow_replies && (message.replies_count !== 0 || messages.length !== 0) && (
                            <div
                                className="text-xs font-bold  cursor-pointer mt-2"
                                onClick={() => {
                                    replying ?
                                        this.setState({ replying: false }) :
                                        this.handleLoadReplies()
                                }}
                            >
                                {replying ? 'Hide' : 'View'} Replies
                                <FontAwesomeIcon
                                    title="Reply"
                                    icon={replying ? faChevronUp : faChevronDown}
                                    size="1x"
                                    onClick={this.handleLoadReplies}
                                    className="cursor-pointer ml-2"
                                />
                            </div>
                        )}
                    </div>
                </div>

                {replying && (
                    <>
                        <div className="h-full mt-4">
                            {messages && messages.map((message, i) => (
                                <ChatMessage
                                    message={message}
                                    sessionId={this.props.sessionId}
                                    key={message.id}
                                    admin={admin}
                                />
                            ))}

                            {meta?.current_page !== meta?.last_page && (
                                <div
                                    className="text-xs font-bold text-center cursor-pointer my-2"
                                    onClick={this.fetchMessages}
                                >
                                    Load more
                                </div>
                            )}

                            {working && (
                                <div className="py-8 text-center text-white">
                                    <FontAwesomeIcon icon={faSpinner} spin size="2x"/>
                                </div>
                            )}
                        </div>

                        <hr className="bg-white"/>

                        {!admin && this.renderNewMessageForm()}
                    </>
                )}
            </div>
        );
    };

    /**
     * @method renderNewMessageForm
     * @return {JSX.Element}
     */
    renderNewMessageForm = () => {
        const { form, working, handleInput, handleSubmit } = this.props;

        return (
            <div className="relative w-full mt-2">
                <Textarea
                    containerClassName="mb-2"
                    rows={3}
                    value={form.message ?? ''}
                    onChange={(v) => handleInput('message', v)}
                    placeholder={translation('misc', 'type_here')}
                    emojiPicker={true}
                />

                <FontAwesomeIcon
                    icon={faPaperPlane}
                    size="1x"
                    onClick={() => !working && form.message ? handleSubmit(
                        null,
                        this.handlePostMessage,
                        (message) => this.handleAddMessage(message.data.data),
                        true,
                    ) : false}
                    className="absolute top-12 right-6 text-black cursor-pointer"
                />
            </div>
        );
    };

    /**
     * @method renderDeleteButton
     * @param {object} message
     * @return {JSX.Element}
     */
    renderDeleteButton = (message) => (
        <ModalTrigger
            component={Delete}
            props={{
                onDelete: () => this.handleDelete(message),
                itemName: 'Message',
            }}
        >
            <button
                className="absolute -right-4 -top-4 w-8 h-8 flex items-center justify-center rounded-full bg-white shadow-md transition duration-200 transform hover:scale-110">
                <FontAwesomeIcon icon={faTrash} className="text-red-500"/>
            </button>
        </ModalTrigger>
    );
}

export default FormHandler(ChatMessage);
